import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSettings, FiShield, FiTool, FiCheckCircle, FiStar } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiSettings,
      title: 'Individuelle Umbauten',
      description: 'Von der Idee bis zur Umsetzung - wir verwirklichen Ihre Motorrad-Träume.',
      features: [
        'Custom Design & Beratung',
        'Motor-Tuning & Performance',
        'Fahrwerk-Modifikationen',
        'Lackierung & Airbrush',
        'LED-Beleuchtung',
        'Auspuffanlagen'
      ],
      image: 'https://lima-service.de/Grafiken/Bilder/3.jpeg'
    },
    {
      icon: FiShield,
      title: 'Service & Reparaturen',
      description: 'Kompletter Kundendienst und professionelle Reparaturen aller Marken.',
      features: [
        'Inspektion & Wartung',
        'Motor-Reparaturen',
        'Getriebe-Service',
        'Bremsen-Service',
        'Reifen-Service',
        'Elektronik-Diagnose'
      ],
      image: 'https://lima-service.de/Grafiken/Bilder/2.jpeg'
    },
    {
      icon: FiTool,
      title: 'TÜV & Prüfungen',
      description: 'Alle technischen Prüfungen und Abnahmen für Ihr Motorrad.',
      features: [
        'TÜV-Hauptuntersuchung',
        'AU-Abgasuntersuchung',
        'Einzelabnahmen',
        'Änderungsabnahmen',
        'Gutachten erstellen',
        'Beratung zu Vorschriften'
      ],
      image: 'https://lima-service.de/Grafiken/Bilder/9.jpg'
    },
    {
      icon: FiStar,
      title: 'Harley Parts & Zubehör',
      description: 'Originale und Custom-Teile für Harley-Davidson und andere Marken.',
      features: [
        'Original Harley Parts',
        'Custom- Teile',
        'Performance-Upgrades',
        'Sitzbank-Umbauten',
        'Lenker & Griffe',
        'Beleuchtung & Elektronik'
      ],
      image: 'https://lima-service.de/Grafiken/Bilder/10.jpg'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-900"
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://lima-service.de/Grafiken/12.jpg')` }} />
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6">
              Unsere <span className="gradient-text">Leistungen</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto">
              Vollservice für Ihr Motorrad - von der Reparatur bis zum Custom Bike
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-20"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                {/* Content */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="flex items-center space-x-4">
                    <motion.div
                      className="w-16 h-16 bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg flex items-center justify-center"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                    </motion.div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-white">
                      {service.title}
                    </h2>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed">{service.description}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        className="flex items-center space-x-2"
                        whileHover={{ x: 5, color: '#f97316' }}
                      >
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-orange-500 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <motion.div
                    className="relative group"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent rounded-xl" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Bereit für Ihr Projekt?
            </h2>
            <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
              Egal ob Reparatur, Umbau oder Neukauf - wir beraten Sie gerne und finden die perfekte Lösung für Ihre Bedürfnisse.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt">
                <motion.div
                  whileHover={{ scale: 1.1, boxShadow: "0 0 20px rgba(255,255,255,0.5)" }}
                  whileTap={{ scale: 0.9 }}
                  className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover-lift flex items-center justify-center"
                >
                  Kostenlose Beratung
                </motion.div>
              </Link>
              <motion.a
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                href="tel:017632543593"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-orange-600 transition-all duration-300 flex items-center justify-center"
              >
                Jetzt anrufen
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Services;